import { useCallback } from 'react'
import { useIsUnmounted, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { useEditingStateWithCtx } from '@/atoms'
import { updateTerritorySettings } from '@/features/domain/territory'
import { DriverActivityFormValues } from '../types'
import { useTexts } from './useTexts'

export function useOnSubmit() {
  const isUnmounted = useIsUnmounted()
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const texts = useTexts()
  const { resetEditing } = useEditingStateWithCtx('setup')

  const updateDriverActivity = useCallback(
    async (driverActivityValues: {
      reasons: string[]
      trackStartEnd: boolean
      enableDynamicLoads: boolean
      enableDynamicCustomFields: boolean
    }) => {
      try {
        const result = await dispatch(updateTerritorySettings(driverActivityValues))

        if (updateTerritorySettings.rejected.match(result)) {
          throw new Error(result.payload?.message ?? 'Internal error')
        }

        toast.success(texts.resultOk)
        if (isUnmounted()) return

        // if the update has been completed successfully let's close the form
        resetEditing()
        return true
      } catch (e) {
        const msg = e instanceof Error ? e.message : JSON.stringify(e)
        toast.error(msg)
        if (isUnmounted()) return
        return false
      }
    },
    [dispatch, isUnmounted, resetEditing, toast, texts],
  )

  return useCallback(
    async (formValues: DriverActivityFormValues) => {
      const {
        reasonsForNotCompletedOrders,
        collectStartEndRoute,
        enableDynamicLoads,
        enableDynamicCustomFields,
      } = formValues

      const nonEmptyReasons = reasonsForNotCompletedOrders.filter(Boolean)

      await updateDriverActivity({
        reasons: nonEmptyReasons,
        trackStartEnd: collectStartEndRoute,
        enableDynamicLoads,
        enableDynamicCustomFields,
      })
    },
    [updateDriverActivity],
  )
}
